import React from "react";
import { Link } from "react-router-dom";
import { MapPin, MonitorPlay, CalendarDays, Play, RotateCcw, Ticket } from "lucide-react";
import { QrCodeMock } from "@/components/QrCodeMock";
import { Countdown } from "@/components/Countdown";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const TICKET_STATUS = {
  upcoming: { label: "Upcoming", className: "bg-brand/10 text-brand" },
  active: { label: "Active", className: "bg-live text-white" },
  expired: { label: "Expired", className: "bg-slate-200 text-slate-600" },
  replay: { label: "Replay Available", className: "bg-indigo-100 text-indigo-700" },
};

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

export function MyTicketCard({ item }) {
  const { event, ticket, status } = item;
  const phase = status.phase;
  const isInPerson = ticket.type === "inPerson";
  const st = TICKET_STATUS[phase];

  return (
    <div
      data-testid={`my-ticket-${event.id}-${ticket.type}`}
      className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm"
    >
      <div className="flex flex-col sm:flex-row">
        {/* Left media */}
        <div className="relative h-40 w-full shrink-0 sm:h-auto sm:w-44">
          <img src={event.cover} alt={event.title} className="h-full w-full object-cover" />
          <span
            className={cn(
              "absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide",
              st.className
            )}
          >
            {phase === "active" && <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" />}
            {st.label}
          </span>
        </div>

        {/* Body */}
        <div className="flex flex-1 flex-col gap-3 p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <span
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold",
                  isInPerson ? "bg-brand/10 text-brand" : "bg-purple-100 text-purple-600"
                )}
              >
                {isInPerson ? <MapPin className="h-3.5 w-3.5" /> : <MonitorPlay className="h-3.5 w-3.5" />}
                {isInPerson ? "In person" : "Online"}
              </span>
              <h3 className="mt-2 font-heading text-lg font-bold tracking-tight text-foreground">
                {event.title}
              </h3>
              <p className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
                <CalendarDays className="h-4 w-4 text-brand" /> {fmtDate(event.startsAt)}
              </p>
              <p className="text-sm text-muted-foreground">
                {event.location.name}, {event.location.city}
              </p>
            </div>

            {/* QR for in-person */}
            {isInPerson && (
              <div className="hidden shrink-0 flex-col items-center sm:flex">
                <QrCodeMock seed={`${event.id}-${ticket.type}`} size={104} />
                <span className="mt-1 text-[10px] font-medium text-muted-foreground">
                  Scan at entry
                </span>
              </div>
            )}
          </div>

          {/* Action row */}
          <div className="mt-auto flex flex-wrap items-center gap-3 border-t border-border pt-4">
            {phase === "upcoming" && !isInPerson && (
              <span className="flex items-center gap-2 text-sm text-muted-foreground">
                Access opens in <Countdown target={event.startsAt} variant="inline" className="font-semibold text-foreground" />
              </span>
            )}
            {phase === "upcoming" && isInPerson && (
              <span className="flex items-center gap-2 text-sm text-muted-foreground">
                <Ticket className="h-4 w-4" /> Show this QR at the venue
              </span>
            )}

            {phase === "active" && !isInPerson && (
              <Button asChild data-testid={`enter-live-${event.id}`} className="rounded-full bg-brand hover:bg-brand-hover">
                <Link to={`/live-access/${event.id}`}>
                  <Play className="mr-1.5 h-4 w-4 fill-white" /> Enter live
                </Link>
              </Button>
            )}
            {phase === "active" && isInPerson && (
              <span className="flex items-center gap-2 text-sm font-medium text-live">
                <span className="h-2 w-2 animate-pulse rounded-full bg-live" /> Happening now — head to the venue
              </span>
            )}

            {phase === "replay" && (
              <>
                <Button asChild data-testid={`watch-replay-${event.id}`} variant="outline" className="rounded-full">
                  <Link to={`/live-access/${event.id}`}>
                    <RotateCcw className="mr-1.5 h-4 w-4" /> Watch replay
                  </Link>
                </Button>
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  Expires in{" "}
                  <Countdown target={status.replayExpiresAt} variant="inline" className="font-semibold text-foreground" />
                </span>
              </>
            )}

            {phase === "expired" && (
              <span className="text-sm text-muted-foreground">
                This event has ended{isInPerson ? " · no replay included" : ""}.
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default MyTicketCard;
